/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  HostBinding,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {AsyncValidatorFn, UntypedFormControl, UntypedFormGroup, ValidatorFn} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';
import {EmpZoneDependencyUtils} from '../../emp-zone-dependency.utils';

import {TranslateService} from '@ngx-translate/core';

import {AsyncSubject, BehaviorSubject, EMPTY, merge, Observable, Subject} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  startWith,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import {ATTRIBUTE_MVEL_ALIAS, ProfileMode} from '../../../../app.constants';
import {Destroyable} from '../../../../destroyable.component';
import {EmpContractAttributes} from '../../../../models/models.interfaces';
import {EmpFile} from '../../../../shared/employees/employees.model';
import {FileModel, getFileNameAndExtension, Preview} from '../../../../shared/utils/files';
import {isValueConsideredAsEmpty} from '../../../../shared/utils/lang';
import {Data} from '../../../api';
import {EmployeesService} from '../../../employees/employees.service';
import {LocalizationService} from '../../../localization/localization.service';
import {DataTransformer} from '../../data-transformer/data-transformer';
import {FieldType} from '../../fields/fields.interfaces';
import {FieldRegistry} from '../../fields/registry';
import {TextFieldComponent} from '../../fields/text-field/text-field.component';
import {FormFieldValidationComponent} from '../../form-field-validation/form-field-validation.component';
import {
  EmpZoneDependency,
  EmpZoneDependencyAction,
  GroupZoneDescriptor,
  Value,
  ZoneDescriptor,
  ZoneValues
} from '../../form-generator.interfaces';
import {FormGeneratorService} from '../../form-generator.service';

export class DefaultValue implements Value {
  public value: any;
  public type: string;
}

/**
 * A form field within a card. Displays its label and validation errors, and loads the underlying actual field
 * component (i.e. a text box, or select box, or image picker, etc.).
 */
@Component({
  selector: 'gp-form-card-field',
  providers: [FieldRegistry],
  templateUrl: 'form-card-field.component.html',
  styleUrls: ['./form-card-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormCardFieldComponent extends Destroyable implements AfterViewInit, OnInit {

  public componentRef: ComponentRef<any>;
  public fieldVisible: boolean = true; // for payments modality
  public cardColumns: number = 4;

  public groupZoneDescriptor: GroupZoneDescriptor;
  public descriptor: ZoneDescriptor;
  public originalDescriptor: ZoneDescriptor;
  public control: UntypedFormControl = null;
  public value: Value;
  public readOnly: boolean = false;
  public locale: string;
  public currency: string;
  public editMode: boolean = false;

  public group: UntypedFormGroup;
  public loaded: Observable<FormCardFieldComponent>;

  public loadedInternal: Subject<FormCardFieldComponent> = new AsyncSubject<FormCardFieldComponent>();

  public fieldCls: string;

  @ViewChild('fieldComponent', {read: ViewContainerRef, static: true})
  public fieldComponent: ViewContainerRef;

  @ViewChild(FormFieldValidationComponent, {static: true})
  public formFieldValidation: FormFieldValidationComponent;

  public fieldReady: boolean = false;
  public focused: boolean = false;
  public downloading: boolean = false;
  public groupData: ZoneValues;
  public cardData: ZoneValues[];
  public employeeContracts: Array<Data<EmpContractAttributes>>;
  public isOnBoarding = false;
  public employeeId: number = null;

  public isBlankOrBreak: boolean = false;

  public dependencyTriggered$: BehaviorSubject<EmpZoneDependency> = new BehaviorSubject<EmpZoneDependency>(null);
  public profileMode = ProfileMode.CLASSIC;
  public entityPath: string;

  public calculationActive = false;
  public canCalculate = false;
  public forceCalculate = false;
  public oldValue: any;

  public file: EmpFile;

  public showViewDocumentPanel = false;
  public currentFileName: string;
  public reportDefinitionPreview: Preview;
  public previewError: boolean;
  public loadingPreview: boolean;
  public noSupportedFormat: boolean;
  public supportedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];
  public isGeneratedSimpleForm = false;
  public isHeritageRemoved = false;
  public originalInheritedValue = null;
  public isAiOCRLoading = false;

  public isAppendToBody = false;

  constructor(private element: ElementRef,
              private renderer: Renderer2,
              private componentFactoryResolver: ComponentFactoryResolver,
              private formGeneratorService: FormGeneratorService,
              private registry: FieldRegistry,
              private employeesService: EmployeesService,
              public changeDetectorRef: ChangeDetectorRef,
              private localization: LocalizationService,
              private translate: TranslateService,
              private cd: ChangeDetectorRef,
              private sanitizer: DomSanitizer) {
    super();
    this.loaded = <Observable<any>>this.loadedInternal;
  }

  public ngOnInit() {
    if (this.descriptor && this.descriptor.type === 'CONTRACT' && !this.employeeContracts) {
      this.employeesService.selectedEmployeeContracts.pipe(
        takeUntil(this.componentDestroyed$)
      ).subscribe((value: Array<Data<EmpContractAttributes>>) => {
        this.employeeContracts = value;
      });
    }

    merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeNumberFormatEvent
    )
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.changeDetectorRef.markForCheck());

    this.isBlankOrBreak = this.descriptor.type === 'BLANK' || this.descriptor.type === 'BREAK_LINE';

    switch (this.cardColumns / this.descriptor.nbColumns) {
      case 4:
        this.fieldCls = 'col-md-3 col-xs-12';
        break;
      case 3:
        this.fieldCls = 'col-md-4 col-xs-12';
        break;
      case 2:
        this.fieldCls = 'col-md-6 col-xs-12';
        break;
      case 1.5:
        this.fieldCls = 'col-md-8 col-xs-12';
        break;
      case (1 / 3):
        this.fieldCls = 'col-md-9 col-xs-12';
        break;
      case 1:
      default:
        this.fieldCls = 'col-md-12 col-xs-12';
        break;
    }

    this.forceCalculate = this.isForceCalculation();
    if (this.forceCalculate) {
      if(this.originalDescriptor.isRequired) {
        this.descriptor.isRequired = !this.readOnly;
      } else {
        this.descriptor.isRequired = false;
      }
      this.descriptor.readOnly = true;
    }
    this.canCalculate = this.isCalculationPossible();
    if (this.canCalculate) {
      this.setFieldAutoCalculation();
      this.descriptor.readOnly = true;
    } else if (this.forceCalculate) {
      this.calculationActive = true;
    }
    if (this.descriptor.repInheritedValue && this.groupData?.default && !isValueConsideredAsEmpty(this.value.value)) {
      this.readOnly = true;
    }

    this.dependencyTriggered$
      .pipe(
        tap((dependency) => {
          switch (dependency?.action) {
            case EmpZoneDependencyAction.SET_CALCULATION_DISABLED:
              this.handleFormSetCalculationDisabled(dependency);
              break;
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.employeesService.getEmployeeFileActionButtonsDisabled().pipe(
      tap((toggleButtonAction) => {
        if (toggleButtonAction.action === 'aiOCRResponseLoading') {
          this.setLoading(toggleButtonAction.value);
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public ngAfterViewInit(): any {
    this.load().then(control => {
      this.loadedInternal.next(this);
      this.loadedInternal.complete();

      if ((this.editMode && !this.descriptor.hidden || ['FILE', 'BLANK', 'BREAK_LINE'].includes(this.descriptor.type))
        && !this.fieldReady) {
        this.createField();
      }
      this.changeDetectorRef.markForCheck();
      this.focusField();
      this.defaultValue();
    });

    if (!this.groupZoneDescriptor.switchEditMode) {
      return;
    }

    this.groupZoneDescriptor.switchEditMode
      .asObservable()
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe((isEditMode: boolean) => {
        this.editMode = isEditMode;
        if (isEditMode && !this.descriptor.hidden && !this.fieldReady) {
          this.createField();
        }
        this.changeDetectorRef.markForCheck();

        this.focusField();
        this.defaultValue();
      });
  }

  public downloadFile(event) {
    event.stopImmediatePropagation();

    this.downloading = true;
    this.changeDetectorRef.markForCheck();
    this.employeesService
      .downloadFile(
        this.value.file,
        this.descriptor.metadatas.downloadFileUrl,
        this.descriptor.metadatas.fileUrlsAuthenticated,
        this.profileMode
      ).pipe(
        finalize(() => {
          this.downloading = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe();
  }

  public getRepInheritedTooltip() {
    return this.descriptor.repInheritedValue.split(':')
      .filter(key => !!key)
      .map(key => this.translate.instant(key))
      .join(':');
  }

  private load(): Promise<UntypedFormControl> {

    if (this.value.type === FieldType.BOOLEAN) {
      this.value.value = (this.value.value === 'true' || this.value.value === true) ? true :
        (this.value.value === 'false' || this.value.value === false) ? false : null;
    }

    if (this.descriptor.inputMask) {
      this.value.value = DataTransformer.applyMask(this.value.value, this.descriptor.inputMask);
    }
    this.control = new UntypedFormControl(this.value.value);

    const validators: ValidatorFn[] = this.formFieldValidation.buildValidators();

    if (!this.calculationActive) {
      this.control.setValidators(validators);
      if ( this.descriptor.zoneID === 'cliEmp.email' && !this.isOnBoarding) {
        const asyncValidators: AsyncValidatorFn[] = [];
        asyncValidators.push(this.employeesService.uniqueEmailAsyncValidator(this.employeeId));
        this.control.setAsyncValidators(asyncValidators);
      }
      this.control.updateValueAndValidity();

      if (this.value.type === 'ENTITY') {
        this.control.valueChanges.pipe(
          startWith(this.value.value),
          filter((value) => !!value),
          switchMap((value) => this.fetchRepEntityPath(value)),
          takeUntil(this.componentDestroyed$)
        ).subscribe();
      }
    }
    return Promise.resolve(this.control);
  }

  public get displayValue() {
    if (this.value.type === 'CONTRACT') {
      return this.registry.getDisplayValue(this.value, this.descriptor, this.currency, this.employeeContracts);
    } else {
      return this.registry.getDisplayValue(this.value, this.descriptor, this.currency);
    }
  }

  private createField() {
    const component = this.registry.resolve(this.descriptor) || TextFieldComponent;

    this.componentRef = this.fieldComponent.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(component));

    Object.assign(this.componentRef.instance, {
      control: this.control,
      groupZoneDescriptor: this.groupZoneDescriptor,
      descriptor: this.descriptor,
      value: this.value,
      contracts: this.employeeContracts,
      profileMode: this.profileMode,
      readOnly: this.readOnly || this.descriptor.readOnly,
      currency: this.currency,
      locale: this.locale,
      fieldFocused$: this.formGeneratorService.fieldFocused$,
      fieldBlurred$: this.formGeneratorService.fieldBlurred$,
      dependencyTriggered$: this.dependencyTriggered$,
      isViewAttachmentsActive: this.isViewAttachmentsActive(),
      isAppendToBody: this.isAppendToBody
    });

    this.fieldReady = true;
  }

  private focusField() {
    if (this.focused) {
      this.focused = false;

      setTimeout(() => {
        const btn = this.element.nativeElement.querySelector('.fa-calendar-alt, .btn');
        if (btn) {
          btn.click();
          return;
        }
        const input = this.element.nativeElement.querySelector('input, textarea');
        if (input) {
          input.focus();
          return;
        }
      });
    }
  }

  @HostBinding('style.display')
  public get display() {
    return this.descriptor.hidden ? 'none' : 'block';
  }

  private defaultValue() {
    if (this.descriptor.defaultValue) {
      const input = this.element.nativeElement.querySelector('input, textarea');
      if (input) {
        this.renderer.setAttribute(input, ATTRIBUTE_MVEL_ALIAS, this.descriptor.defaultValue);
      }
    }
  }

  public fetchRepEntityPath(value): Observable<string> {
      return this.employeesService.getEntityPath(value).pipe(
        distinctUntilChanged(),
        tap((entityPath: string) => {
          this.entityPath = this.convertEntityPath(entityPath);
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private convertEntityPath(entityPath: string): string {
    if (entityPath) {
      const trimmedPath = entityPath.replace(/^\/|\/$/g, '');
      return trimmedPath.split('/').map((item) => item.replace(/_/g, ' ')).join(' >> ');
    }
    return '';
  }

  public toggleHeritage(): void {

    if (!(this.canCalculate || (this.descriptor.repInheritedValue || this.originalInheritedValue))) {
      return;
    }
    this.isHeritageRemoved = !this.isHeritageRemoved;
    this.cd.markForCheck();


    if (this.isHeritageRemoved) {
      if (this.originalInheritedValue === null) {
        this.originalInheritedValue = cloneDeep(this.value);
      }

      if (this.componentRef) {
        this.componentRef.instance.readOnly = false;
        this.readOnly = false;
        const componentChangeDetector = this.componentRef.injector.get(ChangeDetectorRef);
        componentChangeDetector?.detectChanges();
      }
      this.focused = true;
      this.focusField();
    } else {

      if (this.canCalculate) {
        this.value = { ...this.value, value: '', transId: null };
        this.control?.setValue('');
        this.employeesService.requestRecalculation();
      } else if (this.originalInheritedValue !== null) {
        this.value = this.originalInheritedValue;
        this.control?.setValue(this.value.value);
      }

      if (this.componentRef) {
        this.componentRef.instance.readOnly = true;
        this.readOnly = true;

        const componentChangeDetector = this.componentRef.injector.get(ChangeDetectorRef);
        componentChangeDetector?.detectChanges();
      }
    }
  }

  public setFieldAutoCalculation() {
    if (isValueConsideredAsEmpty(this.value?.value)) {
      this.calculationActive = true;
    }
  }

  private isCalculationPossible() {
    return this.isCalculationAllowed()
      && this.descriptor.calculation?.calculationType === 'ASK_CALCULATION';
  }

  private isForceCalculation() {
    return this.isCalculationAllowed()
      && this.descriptor.calculation?.calculationType === 'FORCE_CALCULATION';
  }

  private isCalculationAllowed() {
    return !this.isOnBoarding
      && this.profileMode === ProfileMode.CLASSIC;
  }

  public viewFile(event): void {
    event.stopImmediatePropagation();

    if (!this.showViewDocumentPanel) {
      this.showViewDocumentPanel = true;
      this.previewDocument().subscribe();
    }
  }

  public isViewAttachmentsActive(): boolean {
    return !this.isOnBoarding && !this.isGeneratedSimpleForm && this.profileMode === ProfileMode.CLASSIC;
  }

  public isSupportedFileType(fileName: string): boolean {
    const fileExtension = getFileNameAndExtension(fileName).fileExtension.toLowerCase();
    return this.supportedFileExtensions.includes(fileExtension);
  }

  public onCloseSidePanel() {
    this.showViewDocumentPanel = false;
    this.reportDefinitionPreview = null;
    this.currentFileName = '';
    this.previewError = false;
  }

  private previewDocument() {
    this.loadingPreview = true;
    this.currentFileName = 'placeholder text for skeleton text';

    return this.employeesService
      .getFile(this.value.file, this.descriptor.metadatas.downloadFileUrl, this.descriptor.metadatas.fileUrlsAuthenticated, this.profileMode)
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = getFileNameAndExtension(fileModel.fileName).fileName;
            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
            } else {
              const supportedFileFormatTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
              this.noSupportedFormat = !supportedFileFormatTypes.includes(fileModel.blob?.type);
              this.reportDefinitionPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
              this.previewError = false;
            }
          } else {
            this.previewError = true;
            this.reportDefinitionPreview = null;
            this.currentFileName = '';
          }
        }),
        catchError(() => {
          this.previewError = true;
          this.reportDefinitionPreview = null;
          this.currentFileName = '';
          return EMPTY;
        }),
        finalize(() => {
          this.loadingPreview = false;
          this.cd.detectChanges();
        })
      );
  }

  private getDocumentPreview(href: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${href}"
                     name="${fileName}">
             </iframe>
          `), href: this.sanitizer.bypassSecurityTrustUrl(href), download: fileName
    };
  }

  public isPrefilledField(): boolean {
    return ((!!this.descriptor?.repInheritedValue && this.groupData?.default) || this.canCalculate || this.originalInheritedValue);
  }

  private handleFormSetCalculationDisabled(dependency: EmpZoneDependency) {
    if (EmpZoneDependencyUtils.isReset(dependency)) {
      if (this.descriptor.calculation?.calculationType === 'ASK_CALCULATION') {
        this.canCalculate = true;
        this.descriptor.readOnly = true;
      } else if (this.descriptor.calculation?.calculationType === 'FORCE_CALCULATION') {
        this.forceCalculate = true;
        this.readOnly = true;
        this.descriptor.readOnly = true;
        this.descriptor.isRequired = false;
      }
      this.calculationActive = true;
    } else {
      this.forceCalculate = false;
      this.canCalculate = false;
      this.readOnly = false;
      this.descriptor.readOnly = this.originalDescriptor.readOnly;
      this.descriptor.isRequired = this.originalDescriptor.isRequired;
      this.calculationActive = false;
    }

    if (this.componentRef?.instance) {
      this.componentRef.instance.readOnly = this.readOnly || this.descriptor.readOnly;
    }

    if (this.control) {
      if (this.calculationActive) {
        this.control.clearValidators();
        this.control.clearAsyncValidators();
        this.control.setErrors(null);
      } else {
        this.control.setValidators(this.formFieldValidation.buildValidators());

        if (this.descriptor.zoneID === 'cliEmp.email' && !this.isOnBoarding) {
          this.control.setAsyncValidators([this.employeesService.uniqueEmailAsyncValidator(this.employeeId)]);
        } else {
          this.control.clearAsyncValidators();
        }
      }

      this.control.updateValueAndValidity({ emitEvent: false });
    }

    this.changeDetectorRef.markForCheck();
  }

  public getCalculatedFieldTooltip(): string {
    const calculation = (this.descriptor as any)?.calculation;

    if (!calculation?.dependencies || calculation.dependencies.length === 0) {
      return '';
    }

    const dependencyList = calculation.dependencies
      .map(depId => {
        const zoneData = this.groupZoneDescriptor.zones.find(zone => zone.zoneID === depId);
        return zoneData ? zoneData.transId : depId;
      })
      .filter(label => !!label);

    if (dependencyList.length === 0) {
      return '';
    }

    return this.translate.instant('FORMS.child_of') + dependencyList.join(', ');
  }

  public generateWithAiUsingFile(empFile: EmpFile, event?: MouseEvent): void {
    /* fake function. Will be overrided inside the form-card-component */
  }

  public setLoading(isLoading: boolean): void {
    this.isAiOCRLoading = isLoading;
    this.cd.markForCheck();
  }

  public shouldShowAiIcon(): boolean {
    const shouldShow = !this.isOnBoarding && this.descriptor.type === 'FILE' && !!this.value?.file
      && this.editMode
      && (this.groupZoneDescriptor.internalType === 'BANKING_DETAILS' || this.groupZoneDescriptor.groupZoneID.startsWith('Address:'))
      && this.profileMode === ProfileMode.CLASSIC;

    return shouldShow;
  }

}
